import { FC, useContext, useEffect, useRef, useState } from "react";
import { useMutation } from "react-query";
import client from "src/api";
import { getErrorMessage } from "src/lib/utils";
import GlobalContext from "src/context/global-context";
import { APISomethingWrongMsg, NetworkDisconnectMsg } from "src/lib/constants";
import useAuth from "src/hooks/use-auth";
import Button from "src/components/ui/button";
import { TwoFactorAuthIcon } from "src/components/icons/2fa-icon";
import { GreenTickIcon } from "src/components/icons/green-tick-icon";
import { InfoIcon } from "src/components/icons/info-icon";
import TwoAuthSettingsConfiguration from "src/components/settings/two-auth-configuration";

const ProfileTwoFAPage: FC = () => {
  const { getCurrrentUser, setCurrentUser } = useAuth();
  const currentUser = getCurrrentUser() || {};
  const isMountedRef = useRef(true);
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);

  const [is2FAEnabled, set2FAEnabled] = useState<number>(
    currentUser.google2fa_enabled ? 1 : 0
  );
  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const [showConfiguration, setShowConfiguration] = useState(false);
  const [secretKey, setSecretKey] = useState("");
  const [qrImage, setQRImage] = useState("");

  const fetch2FAStatus = async () => {
    setIsStatusLoading(true);
    try {
      const res = await client.google2FA.getStatus();
      const rawData = res?.data || res;
      const isEnabled = Boolean(rawData?.google2fa_enabled);
      if (!isMountedRef.current) return;

      set2FAEnabled(isEnabled ? 1 : 0);
      if (isEnabled) {
        setShowConfiguration(false);
      }

      const user = getCurrrentUser();
      if (user) {
        setCurrentUser(
          { ...user, google2fa_enabled: isEnabled ? 1 : 0 },
          user.password
        );
      }
    } catch (err: any) {
      console.error("Failed to fetch 2FA status:", err);
      if (!isMountedRef.current) return;
      if (err.code === "ERR_NETWORK") {
        setAlertText(NetworkDisconnectMsg);
      } else if (err.response?.data) {
        setAlertText(getErrorMessage(err.response.data));
      } else {
        setAlertText(APISomethingWrongMsg);
      }
      setIsAlertOpened(true);
    } finally {
      if (isMountedRef.current) {
        setIsStatusLoading(false);
      }
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    fetch2FAStatus();
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const { mutate: generateSecretKey, isLoading: isLoadingSecretKey } =
    useMutation(client.google2FA.generateKey, {
      onSuccess: (data) => {
        setSecretKey(data.secret);
        setQRImage(data.QR_Image);
        setShowConfiguration(true);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (
            error.response?.data?.errors &&
            error.response.data.errors[0] === "can_not_disable_last_2fa"
          ) {
            setAlertText("You cannot disable 2FA");
            setIsAlertOpened(true);
            return;
          }
          if (error.response?.data) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    });

  const onEnable2FA = () => {
    if (!showConfiguration) {
      if (!is2FAEnabled) {
        generateSecretKey();
      }
    } else {
      setShowConfiguration(false);
    }
  };

  return (
    <div className="w-full h-full flex-col p-2 sm:p-4">
      <div className="w-full flex flex-row items-center justify-between mb-4">
        <p className="text-[25px] sm:text-[32px] font-manrope text-primary font-medium leading-[37.5px] sm:leading-[48px]">
          Two-Factor Authentication
        </p>
      </div>

      <div className="flex flex-col bg-white rounded-[12px] p-4 sm:p-6 gap-4 max-w-[1080px] shadow-sm">
        <div className="w-full flex flex-row items-center justify-between">
          <div className="flex flex-row gap-2 items-center">
            <TwoFactorAuthIcon className="w-[24px] h-[24px] text-primary" />
            <p className="font-manrope text-[18px] font-medium text-primary leading-[24px]">
              Two-Factor Authentication (2FA)
            </p>
          </div>
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-[12px] font-manrope font-semibold ${
              is2FAEnabled
                ? "bg-[#E6F9E6] text-[#228B22]"
                : "bg-[#F3F4F6] text-[#6B7280]"
            }`}
          >
            {isStatusLoading
              ? "Checking..."
              : is2FAEnabled
              ? "Enabled"
              : "Disabled"}
          </span>
        </div>

        <div className="w-full">
          <hr className="h-[1px] border-0 bg-[#E6E8EC] opacity-50 dark:opacity-50" />
        </div>

        {is2FAEnabled ? (
          <div className="flex flex-col gap-4 py-2">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-[#F6FDF7] border border-[#B7EB8F] rounded-[12px] p-4 sm:p-5">
              <div className="flex items-start sm:items-center gap-3.5">
                <div className="w-10 h-10 rounded-full bg-[#E6F9E6] border border-[#B7EB8F] flex items-center justify-center flex-shrink-0">
                  <GreenTickIcon className="w-5 h-5 text-[#228B22]" />
                </div>
                <div className="flex flex-col gap-0.5">
                  <h4 className="font-manrope text-[16px] font-bold text-primary leading-[24px]">
                    2FA is Currently Enabled
                  </h4>
                  <p className="font-manrope text-secondary text-[14px] font-medium leading-[22px]">
                    Your account is fully secured. Two-factor authentication is active and protecting your login and sensitive actions.
                  </p>
                </div>
              </div>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-manrope font-semibold bg-[#E6F9E6] text-[#228B22] border border-[#B7EB8F] flex-shrink-0">
                <span className="w-2 h-2 rounded-full bg-[#228B22] inline-block" />
                Active & Secured
              </span>
            </div>

            <div className="flex flex-row items-center gap-2.5 p-3.5 bg-[#F9FAFB] rounded-[10px] border border-[#E5E7EB]">
              <InfoIcon className="w-4 h-4 text-secondary flex-shrink-0" />
              <p className="font-manrope text-[13px] font-medium text-secondary leading-[20px]">
                To ensure account security, 2FA cannot be disabled. If you lose access or need to reconfigure your authenticator app, please contact the administrator.
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="font-manrope text-secondary text-[16px] font-medium leading-[24px]">
                For your security, we strongly recommend enabling 2FA on your
                account. After that, you will need to enter a one-time 6-digit
                code each time you are logging into your account, withdrawing
                funds or changing the password.
              </p>
              <Button
                variant="solid"
                type="button"
                onClick={onEnable2FA}
                className="rounded-[10px] font-manrope text-[16px] font-bold leading-[24px] h-[64px] w-full bg-active hover:bg-main sm:max-w-[215px] flex-shrink-0"
                isLoading={isLoadingSecretKey || isStatusLoading}
                disabled={isStatusLoading}
              >
                {showConfiguration ? "Cancel" : "Enable 2FA"}
              </Button>
            </div>

            {showConfiguration && (
              <TwoAuthSettingsConfiguration
                isEnabled={is2FAEnabled}
                secretKey={secretKey}
                qrCode={qrImage}
                set2FAEnabled={(val: number) => {
                  set2FAEnabled(val);
                  fetch2FAStatus();
                }}
                setShowConfiguration={setShowConfiguration}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ProfileTwoFAPage;
